import { withDevtools } from '@angular-architects/ngrx-toolkit';
import { computed, inject, Signal } from '@angular/core';
import { Router } from '@angular/router';
import { NotificationLog, NotificationLogListItem } from '@models/notifications/logs/logs.model';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { catchError, EMPTY, pipe, switchMap, tap } from 'rxjs';
import { getOne, StateDictionary } from '../../helpers';
import { NotificationLogsService } from './logs.service';

// State definition
interface NotificationLogsState {
  isLoading: boolean;
  selectedLog: NotificationLog | null;
  selectedLogLoading: boolean;
}

const initialState: NotificationLogsState = {
  isLoading: false,
  selectedLog: null,
  selectedLogLoading: false,
};

const notificationLogsDictionary: StateDictionary<NotificationLogListItem> = {
  listLoading: 'isLoading',
  entityList: '',
  selectedEntity: 'selectedLog',
  selectedEntityLoading: 'selectedLogLoading',
  entityId: 'id',
  createLoading: '',
};

export const NotificationLogsStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withDevtools('notification-logs-store'),

  withComputed(state => ({
    selectedLogId: computed(() => state.selectedLog()?.id || null),
  })),

  withMethods((store, logsService = inject(NotificationLogsService), router = inject(Router)) => {
    const getOneLog = getOne<NotificationLogListItem>({
      store,
      service: logsService,
      dictionary: notificationLogsDictionary,
    });

    const resendNotification = rxMethod<string>(
      pipe(
        tap(() => patchState(store, { selectedLogLoading: true, isLoading: true })),
        switchMap(logId =>
          logsService.resend(logId).pipe(
            tap(log => {
              patchState(store, {
                selectedLog: log,
                selectedLogLoading: false,
                isLoading: false,
              });
            }),
            catchError(() => {
              patchState(store, { selectedLogLoading: false, isLoading: false });
              return EMPTY;
            }),
          ),
        ),
      ),
    );

    const sendToAnotherEmail = rxMethod<{ logId: string; email: string }>(
      pipe(
        tap(() => patchState(store, { selectedLogLoading: true, isLoading: true })),
        switchMap(({ logId, email }) =>
          logsService.sendToAnotherEmail(logId, email).pipe(
            tap(() => patchState(store, { selectedLogLoading: false, isLoading: false })),
            catchError(() => {
              patchState(store, { selectedLogLoading: false, isLoading: false });
              return EMPTY;
            }),
          ),
        ),
      ),
    );

    // Navigation
    const goToLogActions = async (logId: string): Promise<boolean> => {
      const success = await router.navigate(['notifications', 'logs', logId]);
      if (success && !store.selectedLogLoading()) {
        getOneLog(logId);
      }
      return success;
    };

    const goToLogsListPage = () => {
      patchState(store, { selectedLog: null });
      router.navigate(['notifications', 'logs']);
    };

    return {
      getOneLog,
      resendNotification,
      sendToAnotherEmail,
      goToLogActions,
      goToLogsListPage,
    };
  }),
);

export interface NotificationLogsStoreType {
  // state signals
  readonly isLoading: Signal<boolean>;
  readonly selectedLog: Signal<NotificationLog | null>;
  readonly selectedLogLoading: Signal<boolean>;

  // computed signals
  readonly selectedLogId: Signal<string | null>;

  // methods
  readonly getOneLog: (logId: string) => void;
  readonly resendNotification: (logId: string) => void;
  readonly sendToAnotherEmail: (input: { logId: string; email: string }) => void;
  readonly goToLogActions: (logId: string) => Promise<boolean>;
  readonly goToLogsListPage: () => void;
}
